// src/components/pickers/ManoperaPickerModal.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Alert,
} from "react-bootstrap";
import { authFetch } from "../utils/authFetch";

import { skillLabel } from "../comenzi/laborSkills";

async function authGetJson(url, fetchClient) {
  const r = await fetchClient(url);
  if (r && typeof r === "object" && !("ok" in r) && !("json" in r)) return r;
  if (r?.ok) return await r.json();
  const msg = r?.text ? await r.text() : "Request failed";
  throw new Error(msg);
}

export default function ManoperaPickerModal({
  skills,
  fetchClient = authFetch,
  show,
  onHide,
  onPick,
  initialQuery = "",
}) {
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState(initialQuery);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState(1);

  const loadingPageRef = useRef(null);
  const lastFetchedQueryRef = useRef(null);
  const limit = 20;

  const fetchPage = async (nextPage = 1, query = q) => {
    const qq = (query || "").trim();
    if (loadingPageRef.current === nextPage) {
      return;
    }
    loadingPageRef.current = nextPage;

    setBusy(true);
    setErr("");
    try {
      const params = new URLSearchParams({
        skip: String((nextPage - 1) * limit),
        limit: String(limit),
      });
      if (qq) {
        params.set("search", qq);
      }

      const url = `/comenzi/manopere-lk?${params.toString()}`;
      const data = await authGetJson(url, fetchClient);

      const list = data?.items || [];
      const t = data?.total ?? null;

      const mapped = list.map((it) => {
        return {
          id: it.bxid,
          code: it.bxcod,
          name: it.bxmanopera,
          skill: it.bxskill,
          minute: it.bxminute,
          label: `${it.bxcod} • ${it.bxmanopera} (${it.bxskill == null ? "Skill nespecificat" : `Skill ${it.bxskill}`})`
        };
      });

      setItems((prev) => (nextPage === 1 ? mapped : [...prev, ...mapped]));
      setTotal(t);
      setPage(nextPage);
      lastFetchedQueryRef.current = qq;
    } catch (e) {
      setErr(String(e?.message || e));
      if (nextPage === 1) {
        setItems([]);
        setTotal(null);
      }
    } finally {
      setBusy(false);
      loadingPageRef.current = null;
    }
  };

  useEffect(() => {
    if (!show) return;

    const initQ = initialQuery || "";
    setEditor(null);
    setQ(initQ);
    setItems([]);
    setTotal(null);
    setPage(1);
    setErr("");
    lastFetchedQueryRef.current = null;

    fetchPage(1, initQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const query = q.trim();
    if (lastFetchedQueryRef.current === query) {
      return;
    }

    const t = setTimeout(() => {
      fetchPage(1, q);
    }, 300);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, show]);

  const hasMore = total != null ? page * limit < total : items.length === limit;

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 15) {
      if (!busy && hasMore) {
        fetchPage(page + 1, q);
      }
    }
  };

  const pick = (opt) => {
    onPick?.(opt);
    onHide?.();
  };

  const saveCode = async event => {
    event.preventDefault();
    if (saving) return;
    const minute = editor.bxminute === '' ? null : Number(editor.bxminute);
    if (!editor.bxcod.trim() || !editor.bxmanopera.trim() ||
        (minute !== null && (!Number.isFinite(minute) || minute <= 0))) {
      setErr('Completează codul și denumirea. Timpul normat, dacă este completat, trebuie să fie pozitiv.');
      return;
    }
    setSaving(true); setErr('');
    try {
      const row = await fetchClient('/comenzi/manopere-lk', { method: 'POST', body: JSON.stringify({
        bxcod: editor.bxcod.trim(), bxmanopera: editor.bxmanopera.trim(),
        bxskill: editor.bxskill === '' ? null : Number(editor.bxskill), bxminute: minute
      }) });
      setEditor(null);
      pick({ id: row.bxid, code: row.bxcod, name: row.bxmanopera, skill: row.bxskill, minute: row.bxminute,
        label: `${row.bxcod} • ${row.bxmanopera} (${row.bxskill == null ? 'Skill nespecificat' : `Skill ${row.bxskill}`})` });
    } catch (error) { setErr(error.message || 'Codul de manoperă nu a putut fi salvat.'); }
    finally { setSaving(false); }
  };

  return (
    <Modal show={show} onHide={saving ? () => {} : onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Alege cod manoperă</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}

        <div className="d-flex gap-2">
          <Form.Control
            size="sm"
            style={{ fontSize: "13px", height: "35px" }}
            autoFocus
            placeholder="Caută după cod sau denumire manoperă..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                fetchPage(1, q);
              }
            }}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            style={{
              fontSize: "13px",
              height: "35px",
              whiteSpace: "nowrap",
            }}
            disabled={busy}
            onClick={() => fetchPage(1, q)}
          >
            Refresh
          </Button>
        </div>

        <Button className="mt-2" variant="outline-primary" size="sm" disabled={saving} onClick={() => {
          setErr(''); setEditor({ bxcod: '', bxmanopera: '', bxskill: 1, bxminute: '' });
        }}>Adaugă cod manoperă</Button>
        {editor && <Form onSubmit={saveCode} className="border rounded p-3 mt-3">
          <h6>Cod manoperă nou</h6>
          <fieldset disabled={saving}>
            <Form.Group controlId="picker-new-labor-code"><Form.Label>Cod manoperă</Form.Label>
              <Form.Control required maxLength={50} value={editor.bxcod} onChange={e => setEditor({ ...editor, bxcod: e.target.value })} /></Form.Group>
            <Form.Group controlId="picker-new-labor-name" className="mt-2"><Form.Label>Denumire manoperă</Form.Label>
              <Form.Control required maxLength={200} value={editor.bxmanopera} onChange={e => setEditor({ ...editor, bxmanopera: e.target.value })} /></Form.Group>
            <Form.Group controlId="picker-new-labor-skill" className="mt-2"><Form.Label>Skill</Form.Label>
              <Form.Select value={editor.bxskill} onChange={e => setEditor({ ...editor, bxskill: e.target.value })}>
                <option value="">Nespecificat</option>{[1,2,3,4].map(skill => <option key={skill} value={skill}>{skill} — {skillLabel(skills, skill)}</option>)}
              </Form.Select></Form.Group>
            <Form.Group controlId="picker-new-labor-minutes" className="mt-2"><Form.Label>Minute normate</Form.Label>
              <Form.Control type="number" min="0.001" step="0.001" value={editor.bxminute} onChange={e => setEditor({ ...editor, bxminute: e.target.value })} />
              <Form.Text>Lasă gol dacă timpul nu este stabilit.</Form.Text></Form.Group>
            <Button type="submit" className="mt-3">{saving ? 'Se salvează…' : 'Salvează și alege'}</Button>{' '}
            <Button type="button" variant="secondary" className="mt-3" onClick={() => setEditor(null)}>Renunță</Button>
          </fieldset>
        </Form>}

        <div className="mt-3">
          {busy && page === 1 && items.length === 0 ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner size="sm" />
              <span>Se încarcă...</span>
            </div>
          ) : (
            <>
              <div
                style={{ maxHeight: "400px", overflowY: "auto" }}
                onScroll={handleScroll}
              >
                <Table hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 100 }}>Cod</th>
                      <th>Denumire Manoperă</th>
                      <th style={{ width: 100 }}>Skill</th>
                      <th style={{ width: 100 }}>Ore / Minute</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr
                        key={String(it.id)}
                        role="button"
                        onClick={() => pick(it)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="fw-bold">{it.code}</td>
                        <td>{it.name}</td>
                        <td>
                          <span className={it.skill === 2 ? "text-danger fw-bold" : "text-muted"}>
                            {it.skill == null ? "Nespecificat" : skillLabel(skills, it.skill)}
                          </span>
                        </td>
                        <td>{it.minute == null ? "Necompletat" : `${Number((Number(it.minute) / 60).toFixed(5))} ore / ${Number(it.minute)} min`}</td>
                      </tr>
                    ))}

                    {items.length === 0 && !busy && (
                      <tr>
                        <td colSpan={4} className="text-muted text-center py-3">
                          Nimic găsit.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {busy && page > 1 && (
                <div className="d-flex align-items-center gap-2 mt-2 justify-content-center">
                  <Spinner size="sm" />
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    Se încarcă mai multe...
                  </span>
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center mt-2">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {total != null ? `Total: ${total}` : ""}
                </div>
              </div>
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" disabled={saving} onClick={onHide}>
          Închide
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
